import React, { useCallback, useState } from 'react';
import FullPlanOverlay from './FullPlanOverlay';

// Store only a dismissal, never the internal meeting content. Memory remains a
// session fallback when the browser has disabled persistent storage.
const dismissed = new Set();
function wasDismissed(key) {
  if (dismissed.has(key)) return true;
  try { return localStorage.getItem(key) === '1'; }
  catch (_) { return false; }
}

export default function MeetingPrepIntro({ storageKey, data, workspaceReady, onOpenNotes, returnFocusRef }) {
  const [closed, setClosed] = useState(() => wasDismissed(storageKey));
  const close = useCallback(() => {
    dismissed.add(storageKey);
    try { localStorage.setItem(storageKey, '1'); }
    catch (_) { /* Browser preference: the in-memory dismissal still applies. */ }
    setClosed(true);
  }, [storageKey]);
  if (closed || !workspaceReady || !data) return null;
  const pending = ['queued', 'running'].includes(data.status);
  const ready = !!data.pack;
  const failed = data.status === 'failed';
  // Not modal (Lane 1370): the deck and its chat stay usable while it shows.
  return <FullPlanOverlay onClose={close} returnFocusRef={returnFocusRef} modal={false}
    label="Your meeting prep" className="mpp-intro-root">
    <section className="mpp mpp-intro" data-testid="meeting-prep-intro">
      <header><span className="mpp-eyebrow">Internal meeting prep</span>
        <button type="button" onClick={close} aria-label="Close meeting prep introduction">Close</button></header>
      <h2>Your deck is ready. Here’s your meeting prep.</h2>
      <p className="mpp-intro-status" role="status">
        {pending ? 'Your talking points are being prepared. You can use the deck while they finish.'
          : failed ? (ready ? 'Your previous notes are available. The latest update could not finish.' : 'Your prep pack could not finish. You can try again in Speaker Notes; your deck is ready to use.')
            : ready ? (data.pack.stale ? 'Your saved notes are available. Some slides have changed. Review the marked notes in Speaker Notes.' : 'Your meeting brief and slide-by-slide guide are ready.')
              : 'Generate a prep pack in Speaker Notes whenever you’re ready.'}
      </p>
      <div className="mpp-intro-locations">
        <div><span className="mpp-intro-number">01</span><div><h3>Speaker Notes · below each slide</h3>
          <p>Talking points, responses to pushback, and a transition. Open the full prep pack there for your meeting brief and all slide guides.</p></div></div>
        <div><span className="mpp-intro-number">02</span><div><h3>Download · top right</h3>
          <p>Choose <strong>Meeting prep pack</strong> to keep an internal copy. The download becomes available when your notes are ready.</p></div></div>
      </div>
      <footer><p>For your team only. Never included in supplier downloads.</p>
        <button type="button" className="mpp-intro-primary" onClick={() => { close(); onOpenNotes(); }}>Show Speaker Notes</button></footer>
    </section>
  </FullPlanOverlay>;
}
